/**
 * useTaskbarBadge - Hook to sync notification counts with the desktop taskbar badge
 * 
 * This hook automatically:
 * - Starts badge cycling when the desktop app loads
 * - Updates badge counts whenever email, call, or message counts change
 * - Shows rotating badges: normal → email → normal → phone → normal → chat
 */

'use client';

import { useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  isTauriDesktop, 
  updateTaskbarBadge, 
  startTaskbarBadgeCycling,
  stopTaskbarBadgeCycling,
} from '@/lib/desktop/desktopUtils';
import type { NotificationCounts } from '@/lib/desktop/desktopUtils';

interface UseTaskbarBadgeOptions {
  /** Number of unread emails */
  unreadEmails?: number;
  /** Number of missed calls */
  missedCalls?: number;
  /** Number of unread message threads */
  unreadMessages?: number;
  /** Number of tasks due today */
  tasksDueToday?: number;
  /** Whether to auto-start cycling (default: true) */
  autoStart?: boolean;
}

/**
 * Hook to manage desktop taskbar badge notifications
 * 
 * @example
 * ```tsx
 * // In your main layout or dashboard component
 * useTaskbarBadge({
 *   unreadEmails: emailStore.unreadCount,
 *   missedCalls: callStore.missedCount,
 *   unreadMessages: chatStore.unreadThreads,
 * });
 * ```
 */
export function useTaskbarBadge(options: UseTaskbarBadgeOptions = {}) {
  const {
    unreadEmails = 0,
    missedCalls = 0,
    unreadMessages = 0,
    tasksDueToday = 0,
    autoStart = true,
  } = options;

  const isDesktop = useMemo(() => isTauriDesktop(), []);
  const hasStarted = useRef(false);
  const lastCounts = useRef<NotificationCounts | null>(null);

  // Check if running in desktop on mount
  useEffect(() => {
    if (isDesktop && autoStart && !hasStarted.current) {
      hasStarted.current = true;
      startTaskbarBadgeCycling().catch(console.error);
      console.log('[TaskbarBadge] Badge cycling started');
    }

    return () => {
      // Optionally stop cycling when component unmounts
      // stopTaskbarBadgeCycling().catch(console.error);
    };
  }, [autoStart, isDesktop]);

  // Update badge counts whenever they change
  useEffect(() => {
    if (!isDesktop) return;

    const newCounts: NotificationCounts = {
      unread_emails: unreadEmails,
      missed_calls: missedCalls,
      unread_messages: unreadMessages,
      tasks_due_today: tasksDueToday,
    };

    // Only update if counts have actually changed
    if (
      lastCounts.current?.unread_emails === newCounts.unread_emails &&
      lastCounts.current?.missed_calls === newCounts.missed_calls &&
      lastCounts.current?.unread_messages === newCounts.unread_messages &&
      lastCounts.current?.tasks_due_today === newCounts.tasks_due_today
    ) {
      return;
    }

    lastCounts.current = newCounts;
    updateTaskbarBadge(newCounts).catch(console.error);
  }, [isDesktop, unreadEmails, missedCalls, unreadMessages, tasksDueToday]);

  // Manual update function
  const updateBadge = useCallback((counts: Partial<NotificationCounts>) => {
    if (!isDesktop) return;

    const newCounts: NotificationCounts = {
      unread_emails: counts.unread_emails ?? lastCounts.current?.unread_emails ?? 0,
      missed_calls: counts.missed_calls ?? lastCounts.current?.missed_calls ?? 0,
      unread_messages: counts.unread_messages ?? lastCounts.current?.unread_messages ?? 0,
      tasks_due_today: counts.tasks_due_today ?? lastCounts.current?.tasks_due_today ?? 0,
    };

    lastCounts.current = newCounts;
    updateTaskbarBadge(newCounts).catch(console.error);
  }, [isDesktop]);

  // Start/stop cycling controls
  const startCycling = useCallback(() => {
    if (!isDesktop) return;
    startTaskbarBadgeCycling().catch(console.error);
  }, [isDesktop]);

  const stopCycling = useCallback(() => {
    if (!isDesktop) return;
    stopTaskbarBadgeCycling().catch(console.error);
  }, [isDesktop]);

  return {
    isDesktop,
    updateBadge,
    startCycling,
    stopCycling,
  };
}

export default useTaskbarBadge;
